import { defineStore } from "pinia";
import axios from "axios";

export const useCustomerStore = defineStore("customer", {
  state: () => ({
    customer: null,
    isAuthenticated: false,
    loading: false,
    impersonating: false,
    impersonatingName: "",
    userType: null,       // 'customer' | 'employee'
    employeeRole: null,   // 'admin' | 'staff' | null
  }),

  getters: {
    isLoggedIn: (state) => state.isAuthenticated,
    isAdmin: (state) => state.customer?.is_admin === true,
    isEmployee: (state) => state.userType === "employee",
    isEmployeeAdmin: (state) => state.userType === "employee" && state.employeeRole === "admin",
    // Darf die Verwaltung sehen (alle Mitarbeiter + Kunden-Admins)
    canAccessAdmin: (state) => state.customer?.is_admin === true || state.userType === "employee",
    // Darf CRUD-Aktionen auf Kunden/Mitarbeiter (Admin-Mitarbeiter + Kunden-Admins)
    canManage: (state) => state.customer?.is_admin === true || (state.userType === "employee" && state.employeeRole === "admin"),
    hasProject: (state) => !!state.customer?.ablyft_project_id,
  },

  actions: {
    async login(email, password, rememberMe = false) {
      try {
        this.loading = true;
        const r = await axios.post("/api/auth/login", { email, password, rememberMe });
        if (r.data.mustSetPassword) return { success: false, mustSetPassword: true, email: r.data.email, userType: r.data.userType };
        if (r.data.success) {
          localStorage.setItem("auth_token", r.data.token);
          this.customer = r.data.customer;
          this.isAuthenticated = true;
          this.userType = r.data.userType || "customer";
          this.employeeRole = r.data.employeeRole || null;
          return { success: true, userType: this.userType };
        }
        return { success: false, message: r.data.message || "Login fehlgeschlagen" };
      } catch (e) { return { success: false, message: e.response?.data?.message || "Fehler" }; }
      finally { this.loading = false; }
    },

    async setupPassword(email, tempPassword, newPassword) {
      try {
        this.loading = true;
        const r = await axios.post("/api/auth/setup-password", { email, tempPassword, newPassword });
        if (r.data.success) {
          localStorage.setItem("auth_token", r.data.token);
          this.customer = r.data.customer;
          this.isAuthenticated = true;
          this.userType = r.data.userType || "customer";
          this.employeeRole = r.data.employeeRole || null;
          return { success: true, userType: this.userType };
        }
        return { success: false, message: r.data.message };
      } catch (e) { return { success: false, message: e.response?.data?.message || "Fehler" }; }
      finally { this.loading = false; }
    },

    async logout() {
      try { await axios.post("/api/auth/logout"); } catch (e) {}
      localStorage.removeItem("auth_token");
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_name");
      this.customer = null;
      this.isAuthenticated = false;
      this.impersonating = false;
      this.impersonatingName = "";
      this.userType = null;
      this.employeeRole = null;
    },

    async verifySession() {
      const t = localStorage.getItem("auth_token");
      if (!t) { this.customer = null; this.isAuthenticated = false; return false; }
      try {
        const r = await axios.get("/api/auth/verify");
        if (r.data.success) {
          this.customer = r.data.customer;
          this.isAuthenticated = true;
          this.userType = r.data.userType || "customer";
          this.employeeRole = r.data.employeeRole || null;
          if (localStorage.getItem("admin_token")) {
            this.impersonating = true;
            this.impersonatingName = localStorage.getItem("admin_name") || "";
          }
          return true;
        }
        return false;
      } catch (e) {
        localStorage.removeItem("auth_token");
        this.customer = null;
        this.isAuthenticated = false;
        return false;
      }
    },

    async fetchMe() {
      try {
        const r = await axios.get("/api/auth/me");
        if (r.data.success) {
          this.customer = r.data.customer;
          this.userType = r.data.userType || "customer";
          this.employeeRole = r.data.employeeRole || null;
          return r.data.customer;
        }
      } catch (e) { return null; }
    },

    async changePassword(currentPassword, newPassword) {
      try {
        const r = await axios.post("/api/auth/change-password", { currentPassword, newPassword });
        return { success: r.data.success, message: r.data.message };
      } catch (e) { return { success: false, message: e.response?.data?.message || "Fehler" }; }
    },

    startImpersonate(token, name) {
      const a = localStorage.getItem("auth_token");
      localStorage.setItem("admin_token", a);
      localStorage.setItem("admin_name", this.customer ? `${this.customer.firstname} ${this.customer.lastname}` : "Admin");
      localStorage.setItem("auth_token", token);
      this.impersonating = true;
      this.impersonatingName = name;
      // Während der Impersonation: userType temporär auf customer setzen
      this.userType = "customer";
      this.employeeRole = null;
    },

    stopImpersonate() {
      const a = localStorage.getItem("admin_token");
      if (a) {
        localStorage.setItem("auth_token", a);
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_name");
      }
      this.impersonating = false;
      this.impersonatingName = "";
      // userType/employeeRole wird nach fetchMe() wiederhergestellt
    },
  },
});
